import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { Application } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { BellIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { MultiSelect, MultiSelectOption } from "../ui/multi-select";
import { Label } from "../ui/label";

const NotificationSettings: React.FC = () => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [faultApplications, setFaultApplications] = useState<number[]>([]);
  const [summaryApplications, setSummaryApplications] = useState<number[]>([]);
  const [isUpdatingFaults, setIsUpdatingFaults] = useState<boolean>(false);
  const [isUpdatingSummaries, setIsUpdatingSummaries] = useState<boolean>(false);

  // Convert applications to options for multi-select
  const applicationOptions: MultiSelectOption[] = applications.map((app) => ({
    label: app.application_name,
    value: app.id,
  }));

  const handleFaultApplicationChange = (selectedIds: (string | number)[]) => {
    const applicationIDs = selectedIds.map(id => Number(id));
    setIsUpdatingFaults(true);
    
    ApplicationService.setFaultNotifications(
      applicationIDs,
      null,
      apiKey
    ).subscribe(
      () => {
        setFaultApplications(applicationIDs);
        setIsUpdatingFaults(false);
      },
      (error: any) => {
        console.error("Error updating fault notifications:", error);
        setIsUpdatingFaults(false);
      }
    );
  };

  const handleSummaryApplicationChange = (selectedIds: (string | number)[]) => {
    const applicationIDs = selectedIds.map(id => Number(id));
    setIsUpdatingSummaries(true);
    
    ApplicationService.setSessionSummaries(
      applicationIDs,
      null,
      apiKey
    ).subscribe(
      () => {
        setSummaryApplications(applicationIDs);
        setIsUpdatingSummaries(false);
      },
      (error: any) => {
        console.error("Error updating session summaries:", error);
        setIsUpdatingSummaries(false);
      }
    );
  };

  useEffect(() => {
    if (!apiKey) return;

    setIsLoading(true);

    // Fetch applications and current notification settings in parallel
    Promise.all([
      new Promise((resolve, reject) => {
        ApplicationService.getApplications(null, apiKey).subscribe(
          (data: Application[]) => resolve(data),
          (error: any) => reject(error)
        );
      }),
      new Promise((resolve, reject) => {
        ApplicationService.getFaultNotifications(null, apiKey).subscribe(
          (data: any) => resolve(data?.application_ids || []),
          (error: any) => {
            // If endpoint doesn't exist, just return empty array
            console.warn("Could not fetch fault notifications:", error);
            resolve([]);
          }
        );
      }),
      new Promise((resolve, reject) => {
        ApplicationService.getSessionSummaries(null, apiKey).subscribe(
          (data: any) => resolve(data?.application_ids || []),
          (error: any) => {
            // If endpoint doesn't exist, just return empty array
            console.warn("Could not fetch session summaries:", error);
            resolve([]);
          }
        );
      })
    ]).then(([apps, faultIds, summaryIds]) => {
      setApplications(apps as Application[]);
      setFaultApplications(faultIds as number[]);
      setSummaryApplications(summaryIds as number[]);
      setIsLoading(false);
    }).catch((error) => {
      console.error("Error loading notification settings:", error);
      setIsLoading(false);
    });
  }, [apiKey]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">Notifications</h3>
        <p className="text-sm text-muted-foreground">Configure notification settings for your applications</p>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-5 w-32" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-5 w-40" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Real-Time Faults */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <BellIcon className="w-5 h-5 text-maitai-green" />
                Real-Time Faults
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Get notified immediately when faults are detected in your applications
              </p>
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">
                  Applications to monitor
                </Label>
                <MultiSelect
                  options={applicationOptions}
                  value={faultApplications}
                  onChange={handleFaultApplicationChange}
                  placeholder="Select applications to monitor for faults..."
                  disabled={isUpdatingFaults}
                  searchable={true}
                />
                {faultApplications.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {faultApplications.length} application(s) selected for fault monitoring
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Session End Summaries */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <BellIcon className="w-5 h-5 text-maitai-green" />
                Session End Summaries
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Receive summaries when user sessions end
              </p>
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">
                  Applications to summarize
                </Label>
                <MultiSelect
                  options={applicationOptions}
                  value={summaryApplications}
                  onChange={handleSummaryApplicationChange}
                  placeholder="Select applications to receive session summaries..."
                  disabled={isUpdatingSummaries}
                  searchable={true}
                />
                {summaryApplications.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {summaryApplications.length} application(s) selected for session summaries
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
